import { useEffect, useState } from 'react';
import { useGetDatevSettings, useUpdateDatevSettings, useGetDatevLogs, useDatevEmployeeValidationStatus, useStartDatevEmployeeValidation, useGetValidToken } from '../../graphql/hooks/useAdminDashboard';
import { Button } from '../core/Button';
import { InputField } from '../core/InputField';
import toast from 'react-hot-toast';
import type { UpdateDatevSettingsVariables, DatevLog } from '../../graphql/types/admin_dashboard';
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../../graphql/client';
import { useAuth } from '../../graphql/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';

const toastSuccess = (message: string) =>
  toast.success(message, {
    duration: 3000,
    style: {
      background: '#f0fdf4', border: '1px solid #bbf7d0',
      color: '#15803d', padding: '12px 14px',
      borderRadius: '8px', fontSize: '14px',
    },
    iconTheme: { primary: '#16a34a', secondary: '#f0fdf4' },
  });

const toastError = (message: string) =>
  toast.error(message, {
    duration: 4000,
    style: {
      background: '#fef2f2', border: '1px solid #fecaca',
      color: '#dc2626', padding: '12px 14px',
      borderRadius: '8px', fontSize: '14px',
    },
    iconTheme: { primary: '#dc2626', secondary: '#fef2f2' },
  });

type Section = 'general' | 'api' | 'salary' | 'logs' | 'employees' | 'help';

type FormState = UpdateDatevSettingsVariables & { sandbox: boolean };

const INITIAL_FORM: FormState = {
  sandbox: false,
  mandant_id: '',
  client_id: '',
  client_sandbox_id: '',
  target_system: '',
  document_types: '',
  payroll_base_url: '',
  payroll_sandbox_base_url: '',
  exchange_base_url: '',
  exchange_sandbox_base_url: '',
  salary_type_id_amount: 0,
  salary_type_id_ueberstunden: 0,
  salary_type_id_notdienst: 0,
  salary_type_id_spesen: 0,
  salary_type_id_nachtarbeit: 0,
  salary_type_id_sonntagsarbeit: 0,
};

const STRING_FIELDS: { field: keyof FormState; label: string; section: Section }[] = [
  { field: 'mandant_id',                label: 'Mandanten-ID',              section: 'general' },
  { field: 'client_id',                 label: 'Client-ID (Produktiv)',      section: 'general' },
  { field: 'client_sandbox_id',         label: 'Client-ID (Sandbox)',        section: 'general' },
  { field: 'target_system',             label: 'Zielsystem',                 section: 'general' },
  { field: 'document_types',            label: 'Dokumenttypen',              section: 'general' },
  { field: 'payroll_base_url',          label: 'Payroll API URL',            section: 'api' },
  { field: 'payroll_sandbox_base_url',  label: 'Payroll API URL (Sandbox)',  section: 'api' },
  { field: 'exchange_base_url',         label: 'Exchange API URL',           section: 'api' },
  { field: 'exchange_sandbox_base_url', label: 'Exchange API URL (Sandbox)', section: 'api' },
];

const INT_FIELDS: { field: keyof FormState; label: string }[] = [
  { field: 'salary_type_id_amount',         label: 'Grundbetrag' },
  { field: 'salary_type_id_ueberstunden',   label: 'Überstunden' },
  { field: 'salary_type_id_notdienst',      label: 'Notdienst' },
  { field: 'salary_type_id_spesen',         label: 'Spesen' },
  { field: 'salary_type_id_nachtarbeit',    label: 'Nachtarbeit' },
  { field: 'salary_type_id_sonntagsarbeit', label: 'Sonntagsarbeit' },
];

export function DatevView() {
  const { data, isLoading } = useGetDatevSettings();
  const { mutate: updateSettings, isPending } = useUpdateDatevSettings();

  const { userType } = useAuth();
  const isSuperadmin = userType === 'Superadmin';

  const [activeSection, setActiveSection] = useState<Section>('general');
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const [logsPage, setLogsPage] = useState(1);
  const [logsSearch, setLogsSearch] = useState('');
  const [logsDebouncedSearch, setLogsDebouncedSearch] = useState('');
  const [logsSuccessFilter, setLogsSuccessFilter] = useState<boolean | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const { mutate: startValidation, isPending: isStartingValidation } = useStartDatevEmployeeValidation();
  const queryClient = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => setLogsDebouncedSearch(logsSearch), 400);
    return () => clearTimeout(t);
  }, [logsSearch]);

  const { data: logsData, isLoading: logsLoading } = useGetDatevLogs({
    first: 20,
    page: logsPage,
    search: logsDebouncedSearch || null,
    success: logsSuccessFilter,
  });

  const logs = logsData?.data ?? [];
  const logsPaginator = logsData?.paginatorInfo;


  useEffect(() => {
    if (data) {
      setForm({
        sandbox: data.sandbox,
        mandant_id: data.mandant_id,
        client_id: data.client_id,
        client_sandbox_id: data.client_sandbox_id,
        target_system: data.target_system,
        document_types: data.document_types,
        payroll_base_url: data.payroll_base_url,
        payroll_sandbox_base_url: data.payroll_sandbox_base_url,
        exchange_base_url: data.exchange_base_url,
        exchange_sandbox_base_url: data.exchange_sandbox_base_url,
        salary_type_id_amount: data.salary_type_id_amount,
        salary_type_id_ueberstunden: data.salary_type_id_ueberstunden,
        salary_type_id_notdienst: data.salary_type_id_notdienst,
        salary_type_id_spesen: data.salary_type_id_spesen,
        salary_type_id_nachtarbeit: data.salary_type_id_nachtarbeit,
        salary_type_id_sonntagsarbeit: data.salary_type_id_sonntagsarbeit,
      });
    }
  }, [data]);

  const set = (field: keyof FormState, value: string | boolean | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {};

    STRING_FIELDS.forEach(({ field, label }) => {
      if (!String(form[field] ?? '').trim()) {
        errors[field] = `${label} ist erforderlich`;
      }
    });

    INT_FIELDS.forEach(({ field, label }) => {
      const v = form[field];
      if (!v || Number(v) === 0) {
        errors[field] = `${label} ist erforderlich`;
      }
    });

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0] as keyof FormState;
      const stringMatch = STRING_FIELDS.find((f) => f.field === firstErrorField);
      if (stringMatch) {
        setActiveSection(stringMatch.section);
      } else {
        setActiveSection('salary');
      }
    }

    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
  const handleFocus = () => {
    queryClient.invalidateQueries({ queryKey: ['datev-valid-token'] });
  };
  window.addEventListener('focus', handleFocus);
  return () => window.removeEventListener('focus', handleFocus);
}, [queryClient]);

  const handleSave = () => {
    if (!validate()) return;

    updateSettings(form, {
      onSuccess: () => toastSuccess('DATEV-Einstellungen gespeichert'),
      onError: (error: any) => {
        const msg =
          error?.response?.errors?.[0]?.extensions?.debugMessage ||
          error?.response?.errors?.[0]?.message ||
          'Fehler beim Speichern';
        toastError(msg);
      },
    });
  };

  const now = new Date();
  const [validateMonth, setValidateMonth] = useState(now.getMonth() + 1);
  const [validateYear, setValidateYear] = useState(now.getFullYear());
  const { data: hasValidToken } = useGetValidToken();

  const {
    data: validationStatus,
    error: validationStatusError,
    isTimedOut: isValidationTimedOut,
    resumePolling: resumeValidationPolling,
  } = useDatevEmployeeValidationStatus({ month: validateMonth, year: validateYear });

  const validationResults = validationStatus?.results ?? [];
  const isValidationRunning = validationStatus?.status === 'running' && !isValidationTimedOut;
  const isValidating = isStartingValidation || isValidationRunning;
  const lastValidationCheckedAt = validationResults[0]?.checked_at ?? validationStatus?.finished_at;

  const handleValidate = () => {
    startValidation(
      { month: validateMonth, year: validateYear },
      {
        onSuccess: () => resumeValidationPolling(),
        onError: (error: any) => {
          const msg =
            error?.response?.errors?.[0]?.extensions?.debugMessage ||
            error?.response?.errors?.[0]?.message ||
            'Fehler bei der Validierung';

          if (msg === 'Login required') {
            toastError('DATEV-Login erforderlich. Bitte melden Sie sich zunächst bei DATEV an.');
          } else {
            toastError(msg);
          }
        },
      }
    );
  };

  const { token } = useAuth();

  const useDatevLogout = () => {
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const logout = async (options?: { onSuccess?: () => void; onError?: () => void }) => {
      setIsLoggingOut(true);
      try {
        const res = await fetch(`${DEFAULT_ENDPOINT_URL_EXPORT}/datev/logout`, {
          method: 'POST',
          headers: {
          Authorization: token,
        },
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.message || 'Fehler beim Logout');
        }

        await queryClient.invalidateQueries({ queryKey: ['datev-valid-token'] });

        toast.success('DATEV-Verbindung erfolgreich getrennt', {
          duration: 3000,
          style: {
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            color: '#15803d', padding: '12px 14px',
            borderRadius: '8px', fontSize: '14px',
          },
          iconTheme: { primary: '#16a34a', secondary: '#f0fdf4' },
        });

        options?.onSuccess?.();
      } catch (err: any) {
        toast.error(err?.message || 'Fehler beim Trennen der DATEV-Verbindung', {
          duration: 4000,
          style: {
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#dc2626', padding: '12px 14px',
            borderRadius: '8px', fontSize: '14px',
          },
          iconTheme: { primary: '#dc2626', secondary: '#fef2f2' },
        });

        options?.onError?.();
      } finally {
        setIsLoggingOut(false);
      }
    };

    return { logout, isLoggingOut };
  };

  const { logout: datevLogout, isLoggingOut } = useDatevLogout();


  const handleLogout = () => {
    datevLogout();
  };

  const handleSectionChange = (section: Section) => {
    setActiveSection(section);
    setFormErrors({});
  };

  const sections = [
    { id: 'general'   as Section, label: 'Allgemein' },
    { id: 'api'       as Section, label: 'API & URLs' },
    { id: 'salary'    as Section, label: 'Lohnarten' },
    { id: 'logs'      as Section, label: 'Logs' },
    { id: 'employees' as Section, label: 'Mitarbeiter' },
    { id: 'help'      as Section, label: 'Hilfe' },
  ];

  const ErrorMsg = ({ field }: { field: keyof FormState }) =>
    formErrors[field] ? (
      <div className="text-red-500 text-[12px] mt-1">{formErrors[field]}</div>
    ) : null;

  const Checkbox = () => (
    <div
      onClick={() => {
        if (isSuperadmin) set('sandbox', !form.sandbox);
      }}
      className={`w-[18px] h-[18px] rounded-[4px] border-2 flex items-center justify-center transition-colors ${
        isSuperadmin ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
      } ${
        form.sandbox ? 'bg-[#0084c7] border-[#0084c7]' : 'bg-white border-[#d1d5db]'
      }`}
    >
      {form.sandbox && (
        <svg className="w-[10px] h-[10px] text-white" viewBox="0 0 12 12" fill="none">
          <path
            d="M2 6L5 9L10 3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );

  const Spinner = ({ className = '' }: { className?: string }) => (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      style={{ animation: 'spin 0.8s linear infinite' }}
    >
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );

  if (isLoading) {
    return (
      <div className="py-12 flex items-center justify-center gap-2 text-[14px] text-[#6b6b6b]">
        <Spinner className="size-4" />
        Einstellungen werden geladen...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[20px] font-medium text-[#1c1c1c]">DATEV Einstellungen</h2>
          {data?.updated_at && (
            <p className="text-[13px] text-[#6b6b6b] mt-0.5">
              Zuletzt geändert: {new Date(data.updated_at).toLocaleString('de-DE')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasValidToken ? (
            <Button
              variant="secondary"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? 'Wird getrennt...' : 'DATEV Abmelden'}
            </Button>
          ) : (
            <a
              href={`${DEFAULT_ENDPOINT_URL_EXPORT}/datev/login`}
              className="inline-flex items-center h-[44px] px-4 box-border border border-[#0084c7] text-[#0084c7] text-[14px] rounded-lg hover:bg-[#0084c7]/5 transition-colors no-underline"
              target="_blank" 
              rel="noopener noreferrer"
            >
              DATEV Anmelden
            </a>
          )}
          {activeSection !== 'logs' && activeSection !== 'employees' && activeSection !== 'help' && (
            <Button variant="primary" onClick={handleSave} disabled={isPending}>
              {isPending ? 'Speichern...' : 'Speichern'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="hidden md:block w-[200px] border-r border-[#e9e9e9] pr-4 space-y-1">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => handleSectionChange(s.id)}
              className={`block w-full text-left px-3 py-2 rounded-lg text-[14px] transition-colors ${
                activeSection === s.id
                  ? 'bg-[#0084c7]/10 text-[#0084c7]'
                  : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex-1 space-y-4">
          {activeSection === 'general' && (
            <>
              {data?.user_name && (
                <p className="text-[16px] font-bold text-[#6b6b6b]">
                  Eingeloggt als {data.user_name}
                </p>
              )}
              <div className="flex items-center justify-between p-4 border border-[#e0e0e0] rounded-lg">
                <p className="text-[14px] font-medium text-[#1c1c1c]">Sandbox-Modus</p>
                <Checkbox />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputField
                    label="Mandanten-ID"
                    value={form.mandant_id ?? ''}
                    onChange={(v) => set('mandant_id', v)}
                    disabled={!isSuperadmin}
                  />
                  <ErrorMsg field="mandant_id" />
                </div>
                <div className="space-y-2">
                  <label className="text-[#6b6b6b] text-[14px]">Zielsystem</label>
                  <select
                    value={form.target_system ?? ''}
                    onChange={(e) => set('target_system', e.target.value)}
                    className={`w-full form-select h-[42px] px-4 border rounded-lg text-[14px] ${
                      formErrors.target_system ? 'border-red-400' : 'border-[#e0e0e0]'
                    }`}
                  >
                    <option value="">Bitte auswählen</option>
                    <option value="lodas">LODAS</option>
                    <option value="lug">Lohn & Gehalt</option>
                  </select>
                  <ErrorMsg field="target_system" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputField
                    label="Client-ID (Produktiv)"
                    value={form.client_id ?? ''}
                    onChange={(v) => set('client_id', v)}
                    disabled={!isSuperadmin}
                  />
                  <ErrorMsg field="client_id" />
                </div>
                <div>
                  <InputField
                    label="Client-ID (Sandbox)"
                    value={form.client_sandbox_id ?? ''}
                    onChange={(v) => set('client_sandbox_id', v)}
                    disabled={!isSuperadmin}
                  />
                  <ErrorMsg field="client_sandbox_id" />
                </div>
              </div>

              <div>
                <InputField
                  label="Dokumenttypen (kommagetrennt)"
                  value={form.document_types ?? ''}
                  onChange={(v) => set('document_types', v)}
                />
                <ErrorMsg field="document_types" />
              </div>
            </>
          )}

          {activeSection === 'api' && (
            <>
              <div className="space-y-4">
                <p className="text-[13px] text-[#6b6b6b]">Produktive API-Endpunkte</p>
                <div>
                  <InputField
                    label="Payroll API URL"
                    value={form.payroll_base_url ?? ''}
                    onChange={(v) => set('payroll_base_url', v)}
                    disabled={!isSuperadmin}
                  />
                  <ErrorMsg field="payroll_base_url" />
                </div>
                <div>
                  <InputField
                    label="Exchange API URL"
                    value={form.exchange_base_url ?? ''}
                    onChange={(v) => set('exchange_base_url', v)}
                    disabled={!isSuperadmin}
                  />
                  <ErrorMsg field="exchange_base_url" />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#e9e9e9]">
                <p className="text-[13px] text-[#6b6b6b]">Sandbox API-Endpunkte</p>
                <div>
                  <InputField
                    label="Payroll API URL (Sandbox)"
                    value={form.payroll_sandbox_base_url ?? ''}
                    onChange={(v) => set('payroll_sandbox_base_url', v)}
                    disabled={!isSuperadmin}
                  />
                  <ErrorMsg field="payroll_sandbox_base_url" />
                </div>
                <div>
                  <InputField
                    label="Exchange API URL (Sandbox)"
                    value={form.exchange_sandbox_base_url ?? ''}
                    onChange={(v) => set('exchange_sandbox_base_url', v)}
                    disabled={!isSuperadmin}
                  />
                  <ErrorMsg field="exchange_sandbox_base_url" />
                </div>
              </div>
            </>
          )}

          {activeSection === 'salary' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {INT_FIELDS.map(({ field, label }) => (
                <div key={String(field)}>
                  <InputField
                    label={`Lohnart – ${label}`}
                    value={String(form[field] ?? '')}
                    onChange={(v) => {
                      if (v === '' || /^[0-9]*$/.test(v)) {
                        set(field, v === '' ? 0 : Number(v));
                      }
                    }}
                  />
                  <ErrorMsg field={field} />
                </div>
              ))}
            </div>
          )}

          {activeSection === 'logs' && (
            <div className="space-y-4">

              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  placeholder="URL, Request, Response durchsuchen..."
                  value={logsSearch}
                  onChange={(e) => {
                    setLogsSearch(e.target.value);
                    setLogsPage(1);
                  }}
                  className="h-[42px] px-4 border border-[#e0e0e0] rounded-lg text-[14px] w-72 focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
                />

                <select
                  value={logsSuccessFilter === null ? '' : String(logsSuccessFilter)}
                  onChange={(e) => {
                    setLogsSuccessFilter(e.target.value === '' ? null : e.target.value === 'true');
                    setLogsPage(1);
                  }}
                  className="h-[42px] px-4 border border-[#e0e0e0] rounded-lg text-[14px] form-select"
                >
                  <option value="">Alle Status</option>
                  <option value="true">Erfolgreich</option>
                  <option value="false">Fehlerhaft</option>
                </select>

                {(logsSearch || logsSuccessFilter !== null) && (
                  <button
                    onClick={() => {
                      setLogsSearch('');
                      setLogsSuccessFilter(null);
                      setLogsPage(1);
                    }}
                    className="text-[14px] text-[#6b6b6b] hover:text-[#1c1c1c] underline"
                  >
                    Filter zurücksetzen
                  </button>
                )}
              </div>

              {logsLoading ? (
                <div className="py-8 flex items-center justify-center gap-2 text-[16px] text-[#6b6b6b]">
                  <Spinner className="size-5" />
                  Logs werden geladen...
                </div>
              ) : logs.length === 0 ? (
                <div className="py-8 text-center text-[16px] text-[#6b6b6b]">Keine Logs vorhanden</div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div key={log.id} className="border border-[#e9e9e9] rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[#f5f5f5] transition-colors text-left"
                      >
                        <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[13px] font-medium ${
                          log.success
                            ? 'bg-green-50 text-green-600 border border-green-200'
                            : 'bg-red-50 text-red-600 border border-red-200'
                        }`}>
                          {log.status_code ?? '—'}
                        </span>

                        <span className="shrink-0 text-[14px] font-mono font-medium text-[#1c1c1c] w-10">
                          {log.method}
                        </span>

                        <span className="text-[14px] text-[#6b6b6b] font-mono truncate w-0 flex-1">
                          {log.url}
                        </span>

                        <span className="shrink-0 text-[14px] text-[#6b6b6b] w-14 text-right">
                          {log.duration_ms != null ? `${log.duration_ms}ms` : '—'}
                        </span>

                        <span className="shrink-0 text-[14px] text-[#6b6b6b] w-36 text-right">
                          {new Date(log.request_at).toLocaleString('de-DE')}
                        </span>

                        <svg
                          className={`shrink-0 w-4 h-4 text-[#6b6b6b] transition-transform ${expandedLogId === log.id ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {expandedLogId === log.id && (
                        <div className="border-t border-[#e9e9e9] px-6 py-5 space-y-5 bg-[#fafafa]">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[14px]">
                            <div>
                              <p className="text-[#6b6b6b] mb-1">Request</p>
                              <p className="text-[#1c1c1c]">{new Date(log.request_at).toLocaleString('de-DE')}</p>
                            </div>
                            {log.response_at && (
                              <div>
                                <p className="text-[#6b6b6b] mb-1">Response</p>
                                <p className="text-[#1c1c1c]">{new Date(log.response_at).toLocaleString('de-DE')}</p>
                              </div>
                            )}
                            {log.status_message && (
                              <div>
                                <p className="text-[#6b6b6b] mb-1">Status</p>
                                <p className="text-[#1c1c1c]">{log.status_code} {log.status_message}</p>
                              </div>
                            )}
                            {log.global_transaction_id && (
                              <div>
                                <p className="text-[#6b6b6b] mb-1">Transaction ID</p>
                                <p className="text-[#1c1c1c] font-mono text-[13px]">{log.global_transaction_id}</p>
                              </div>
                            )}
                          </div>

                          {log.request_headers && (
                            <div>
                              <p className="text-[#6b6b6b] text-[14px] mb-2">Request Headers</p>
                              <pre className="text-[13px] bg-white border border-[#e9e9e9] rounded-lg p-4 text-[#1c1c1c] whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto">
                                {JSON.stringify(JSON.parse(log.request_headers), null, 2)}
                              </pre>
                            </div>
                          )}

                          {log.request_body && (
                            <div>
                              <p className="text-[#6b6b6b] text-[14px] mb-2">Request Body</p>
                              <pre className="text-[13px] bg-white border border-[#e9e9e9] rounded-lg p-4 text-[#1c1c1c] whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto">
                                {log.request_body}
                              </pre>
                            </div>
                          )}

                          {log.response_headers && (
                            <div>
                              <p className="text-[#6b6b6b] text-[14px] mb-2">Response Headers</p>
                              <pre className="text-[13px] bg-white border border-[#e9e9e9] rounded-lg p-4 text-[#1c1c1c] whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto">
                                {JSON.stringify(JSON.parse(log.response_headers), null, 2)}
                              </pre>
                            </div>
                          )}

                          {log.response_body && (
                            <div>
                              <p className="text-[#6b6b6b] text-[14px] mb-2">Response Body</p>
                              <pre className="text-[13px] bg-white border border-[#e9e9e9] rounded-lg p-4 text-[#1c1c1c] whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto">
                                {log.response_body}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {logsPaginator && logsPaginator.lastPage > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-[#e0e0e0]">
                  <div className="text-[#6b6b6b] text-[14px]">
                    Zeige{' '}
                    {((logsPaginator.currentPage - 1) * (logsPaginator.perPage ?? 20)) + 1}
                    {' '}bis{' '}
                    {Math.min(logsPaginator.currentPage * (logsPaginator.perPage ?? 20), logsPaginator.total)}
                    {' '}von {logsPaginator.total} Einträgen
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                      disabled={logsPaginator.currentPage === 1}
                      className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
                    >
                      Vorherige
                    </button>

                    {Array.from({ length: logsPaginator.lastPage }, (_, i) => i + 1)
                      .slice(
                        Math.max(0, logsPaginator.currentPage - 3),
                        logsPaginator.currentPage + 2,
                      )
                      .map((page) => (
                        <button
                          key={page}
                          onClick={() => setLogsPage(page)}
                          className={`px-3 py-1 text-[14px] rounded ${
                            logsPaginator.currentPage === page
                              ? 'bg-[#0084c7] text-white'
                              : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
                          }`}
                        >
                          {page}
                        </button>
                      ))}

                    <button
                      onClick={() => setLogsPage((p) => Math.min(logsPaginator.lastPage, p + 1))}
                      disabled={logsPaginator.currentPage === logsPaginator.lastPage}
                      className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
                    >
                      Nächste
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeSection === 'employees' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[16px] font-medium text-[#1c1c1c] mb-1">
                  Mitarbeiter-Validierung
                </h3>
                <p className="text-[14px] text-[#6b6b6b]">
                  Prüft ob die internen Personalnummern der Benutzer im gewählten Zeitraum bei DATEV existieren.
                </p>
              </div>

              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-2 flex gap-4 items-center">
                  <label className="text-[#6b6b6b] text-[14px]">Monat</label>
                  <select
                    value={validateMonth}
                    onChange={(e) => setValidateMonth(Number(e.target.value))}
                    className="h-[42px] px-4 pr-8 border border-[#e0e0e0] rounded-lg text-[14px] form-select min-w-[140px]"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1).toLocaleString('de-DE', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 flex gap-4 items-center">
                  <label className="text-[#6b6b6b] text-[14px]">Jahr</label>
                  <select
                    value={validateYear}
                    onChange={(e) => setValidateYear(Number(e.target.value))}
                    className="h-[42px] px-4 pr-8 border border-[#e0e0e0] rounded-lg text-[14px] form-select min-w-[100px]"
                  >
                    {Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i).map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                <Button variant="primary" onClick={handleValidate} disabled={isValidating}>
                  {isValidating ? 'Wird geprüft...' : 'Jetzt prüfen'}
                </Button>
              </div>

              {isValidating && (
                <div className="py-8 flex flex-col items-center justify-center gap-1 text-[#6b6b6b]">
                  <div className="flex items-center gap-2 text-[16px]">
                    <Spinner className="size-5" />
                    Prüfung läuft...
                  </div>
                  <p className="text-[13px]">Das kann bis zu 2–3 Minuten dauern.</p>
                </div>
              )}

              {isValidationTimedOut && validationStatus?.status === 'running' && (
                <div className="px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-[14px] text-amber-700">
                  Prüfung dauert ungewöhnlich lange, bitte später erneut versuchen.
                </div>
              )}

              {!isValidating && validationStatus?.status === 'failed' && (
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-[14px] text-red-600">
                  <span>{validationStatus.error || 'Die Prüfung ist fehlgeschlagen.'}</span>
                  <Button variant="primary" onClick={handleValidate}>
                    Erneut versuchen
                  </Button>
                </div>
              )}

              {validationStatusError && (
                <div className="px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-[14px] text-red-600">
                  Status der Prüfung konnte nicht geladen werden.
                </div>
              )}

              {!isValidating && validationResults.length > 0 && (
                <div className="space-y-3">
                  {validationStatus?.status !== 'completed' && lastValidationCheckedAt && (
                    <p className="text-[13px] text-[#6b6b6b]">
                      {validationStatus?.status === 'failed' ? 'Ergebnisse der letzten erfolgreichen Prüfung' : 'Letzte Prüfung'} vom{' '}
                      {new Date(lastValidationCheckedAt).toLocaleString('de-DE')}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-[14px]">
                    <span className="text-[#6b6b6b]">
                      {validationResults.length} Mitarbeiter geprüft
                    </span>
                    <span className="text-green-600 font-medium">
                      {validationResults.filter((v) => v.exists_in_datev).length} vorhanden
                    </span>
                    <span className="text-red-600 font-medium">
                      {validationResults.filter((v) => !v.exists_in_datev).length} nicht gefunden
                    </span>
                  </div>

                  <div className="grid grid-cols-[2fr_1fr_1fr] gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]">
                    <div>Name</div>
                    <div>Personalnummer</div>
                    <div>Status</div>
                  </div>

                  {validationResults.map((entry) => (
                    <div
                      key={entry.user.id}
                      className="grid grid-cols-[2fr_1fr_1fr] gap-4 px-4 py-3 text-[14px] items-center hover:bg-[#f5f5f5] transition-colors border-b border-[#f0f0f0] last:border-0"
                    >
                      <div className="text-[#1c1c1c]">{entry.user.full_name}</div>
                      <div className="text-[#6b6b6b] font-mono">{entry.internal_number}</div>
                      <div>
                        {entry.exists_in_datev ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-medium bg-green-50 text-green-600 border border-green-200">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 12 12">
                              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Vorhanden
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-medium bg-red-50 text-red-600 border border-red-200">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 12 12" stroke="currentColor">
                              <path d="M3 3l6 6M9 3l-6 6" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            Nicht gefunden
                          </span>
                        )}
                        {entry.message && (
                          <p className="text-[12px] text-[#6b6b6b] mt-1">{entry.message}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {activeSection === 'help' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-[16px] font-medium text-[#1c1c1c] mb-1">DATEV Hilfe</h3>
                <p className="text-[14px] text-[#6b6b6b]">
                  Weiterführende Dokumentation und Hilfestellungen zur DATEV-Integration.
                </p>
              </div>
              <a
                href="https://wissensplattform.apps.datev.de/help/document/1026710"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 border border-[#e0e0e0] rounded-lg hover:bg-[#f5f5f5] transition-colors group no-underline"
              >
                <div className="flex-1">
                  <p className="text-[14px] font-medium text-[#1c1c1c] group-hover:text-[#0084c7] transition-colors">
                    DATEV Wissensplattform
                  </p>
                  <p className="text-[13px] text-[#6b6b6b] mt-0.5">
                    Offizielle Dokumentation zur DATEV API-Integration
                  </p>
                </div>
                <svg className="w-4 h-4 text-[#6b6b6b] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}